import clsx from "clsx";
import React from "react";
import { HistoryIcon } from "@pd/pd-ui-kit";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";

import SelectIcon from "~/pages/Ivideon/CameraList/ui/SelectIcon";
import Checkbox from "~/shared/ui/Fields/Checkbox";
import { hasPermissionForNameWithTypeCheck } from "~/pages/Ivideon/lib/hasPermissionForNameWithTypeCheck";
import { InfoTooltip } from "~/shared/ui/Tooltip";
import { PermissionKey } from "~/types/Permissions";

interface IvideonCameraColumnsCtx {
  s: Record<string, string>;
  handleAcceptCamera: (cameraId: string, message?: string) => void;
  goHistoryCamera: (cameraIvideonId: string, cameraName: string) => void;
  resetKey: number;
  acceptanceSelectReadOnly: boolean;
  hasTariffCheck: (name: string) => boolean;
}

export const getIvideonCameraColumns = (
  t: TFunction,
  ctx: IvideonCameraColumnsCtx,
): ColumnsType<any> => {
  const {
    s,
    handleAcceptCamera,
    goHistoryCamera,
    resetKey,
    acceptanceSelectReadOnly,
    hasTariffCheck,
  } = ctx;

  return [
    {
      title: "",
      dataIndex: "history",
      key: "history",
      width: 36,
      render: (_: any, record: any) => (
        <div
          className={s.history}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            goHistoryCamera(record.ivideon_id, record.name);
          }}
        >
          <HistoryIcon size={16} fill="#495A64" />
        </div>
      ),
    },
    {
      title: t("ivideonCameras.tableCameras.ivideon_id"),
      dataIndex: "ivideon_id",
      key: "ivideon_id",
    },
    {
      title: t("ivideonCameras.tableCameras.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {t("ivideonCameras.tableCameras.lastStatus")}
          <InfoTooltip
            content={t("ivideonCameras.tableCameras.lastStatusLegend")}
            placement="bottom"
          />
        </span>
      ),
      dataIndex: "lastStatus",
      key: "lastStatus",
      width: 80,
      render: (value: string) => {
        const colorStatus =
          value === "online" ? "green" : value === "offline" ? "red" : "gray";
        return <div className={clsx(s.colorCircle, s[colorStatus])} />;
      },
    },
    {
      title: t("ivideonCameras.tableCameras.isAcceptance"),
      dataIndex: "isAcceptance",
      key: "isAcceptance",
      render: (_: any, record: any) => (
        <SelectIcon
          isAcceptance={record.isAcceptance}
          handleAcceptCamera={handleAcceptCamera}
          cameraId={record.id}
          key={`${record.id}-${record.isAcceptance}-${resetKey}`}
          readOnly={acceptanceSelectReadOnly}
          externalCameraRejectReason={
            !hasTariffCheck(record.name)
              ? t("ivideonCameras.tableCameras.externalCameraRejectReason")
              : undefined
          }
        />
      ),
    },
    {
      title: t("ivideonCameras.tableCameras.totalOfflineMinutes"),
      dataIndex: "totalOfflineMinutes",
      key: "totalOfflineMinutes",
      render: (value: any) => value ?? "",
    },
    {
      title: t("ivideonCameras.tableCameras.deacceptReason"),
      dataIndex: "deacceptReason",
      key: "deacceptReason",
      render: (value: any) => value ?? "-",
    },
  ];
};

export const getIvideonGroupColumns = (
  t: TFunction,
  dataGroups: any[],
  handleCheckboxClick: (groupId: string, name: string) => void,
  checkboxClassName: string,
  hasPermission: (key: PermissionKey) => boolean,
): ColumnsType<any> => [
  {
    title: t("ivideonCameras.tableGroups.name"),
    dataIndex: "name",
    key: "name",
  },
  {
    title: t("ivideonCameras.tableGroups.cameras"),
    dataIndex: "cameras",
    key: "cameras",
    render: (value: any[]) => value?.length ?? 0,
  },
  {
    title: t("ivideonCameras.tableGroups.isAcceptance"),
    dataIndex: "isAcceptance",
    key: "isAcceptance",
    render: (_: any, record: any) => {
      const isChecked = dataGroups?.some(
        (group) => group.id === record.id && group.isAcceptance,
      );

      const hasAccess = hasPermissionForNameWithTypeCheck(
        record.name,
        hasPermission,
      );

      return (
        <Checkbox
          customClassName={checkboxClassName}
          checked={isChecked}
          disabled={!hasAccess || isChecked}
          onCheck={() => handleCheckboxClick(record.id, record.name)}
        />
      );
    },
  },
];
